import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { formatNumber, inputClass, QUANTITY_STEP } from '../ui/FormControls';
import { cn } from '../../lib/utils';

export interface AvailableBatch {
  id: string;
  batchNumber: string;
  heatNumber: string | null;
  millTcNumber: string | null;
  availableQuantity: number;
  unit: string;
  firstReceivedOn: string;
}

// batch id -> the quantity typed for it
export type BatchSelection = Record<string, string>;

export const batchTotal = (selection: BatchSelection | undefined) =>
  Math.round(Object.values(selection ?? {}).reduce((sum, value) => sum + (Number(value) || 0), 0) * 10000) / 10000;

// What the API takes: only the batches with a quantity
export const batchList = (selection: BatchSelection | undefined) =>
  Object.entries(selection ?? {})
    .map(([batchId, value]) => ({ batchId, quantity: Number(value) }))
    .filter((entry) => Number.isFinite(entry.quantity) && entry.quantity > 0);

export const useAvailableBatches = (productId: string | undefined) =>
  useQuery({
    queryKey: ['batches', 'available', productId],
    queryFn: async (): Promise<AvailableBatch[]> => (await api.get(`/batches/available/${productId}`)).data,
    enabled: !!productId,
  });

interface BatchPickerProps {
  productId: string;
  value: BatchSelection;
  onChange: (value: BatchSelection) => void;
  // What the batches are for, in the heading: "Taken from batches"
  label?: string;
  // Used in the ids of the inputs, so that two pickers on one form stay apart
  idPrefix: string;
}

// The batches of a product that still hold stock, each with a box for how much is taken from it.
// Shows nothing when the product has no batches, so untracked products are not cluttered.
export const BatchPicker: React.FC<BatchPickerProps> = ({ productId, value, onChange, label = 'Taken from batches', idPrefix }) => {
  const { data: batches = [] } = useAvailableBatches(productId);
  if (batches.length === 0) return null;

  return (
    <div className="rounded-md border border-gray-200">
      <p className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label} · {formatNumber(batchTotal(value))}
      </p>
      <ul className="divide-y divide-gray-100">
        {batches.map((batch) => {
          const typed = Number(value[batch.id]) || 0;
          const over = typed > batch.availableQuantity;
          return (
            <li key={batch.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <label htmlFor={`${idPrefix}-${batch.id}`} className="min-w-0 text-sm">
                <span className="font-medium text-gray-900">{batch.batchNumber}</span>
                <span className="text-gray-500">
                  {batch.heatNumber && batch.heatNumber !== batch.batchNumber ? ` · heat ${batch.heatNumber}` : ''} · {formatNumber(batch.availableQuantity)} {batch.unit} left
                </span>
                {over && <span className="ml-2 text-xs font-medium text-red-600">more than the batch holds</span>}
              </label>
              <input
                id={`${idPrefix}-${batch.id}`}
                type="number"
                min="0"
                max={batch.availableQuantity}
                step={QUANTITY_STEP}
                value={value[batch.id] ?? ''}
                onChange={(event) => onChange({ ...value, [batch.id]: event.target.value })}
                placeholder="0"
                aria-label={`Quantity from batch ${batch.batchNumber}`}
                className={cn(inputClass, 'w-28 shrink-0 py-1 text-right tabular-nums', over && 'border-red-400')}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
